import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import type { PotreeEngine } from '../../../engine/PotreeEngine'
import { type PhaseRenderContext, RenderPhase } from '../../../shared/types/assets'
import type { RenderContributor, RenderPipeline } from '../../../shared/types/services'
import type { ViewFrameContext } from '../../../shared/types/view'
import type {
  SpatialCapsuleClickToWalkController,
  SpatialCapsuleClickToWalkSnapshot,
} from './SpatialCapsuleClickToWalkController'

const CLICK_TO_WALK_RING_UP = new THREE.Vector3(0, 0, 1)
const CLICK_TO_WALK_RING_OFFSET_M = 0.025
const CLICK_TO_WALK_HOVER_RING_RADIUS_M = 0.32
const CLICK_TO_WALK_TARGET_DISC_RADIUS_M = 0.22
const DEFAULT_HOVER_COLOR = 0xffffff
const DEFAULT_TARGET_COLOR = 0xffffff
const DEFAULT_HOVER_OPACITY = 0.92
const DEFAULT_TARGET_OPACITY = 0.86
const DEFAULT_RENDER_PRIORITY = 12

export interface SpatialCapsuleClickToWalkIndicatorsOptions {
  engine: PotreeEngine
  controller: SpatialCapsuleClickToWalkController
  hoverColor?: THREE.ColorRepresentation
  targetColor?: THREE.ColorRepresentation
  hoverOpacity?: number
  targetOpacity?: number
  hoverRadius?: number
  targetRadius?: number
  renderPriority?: number
}

/** Renders click-to-walk hover and target indicators from controller snapshots. */
export class SpatialCapsuleClickToWalkIndicators {
  readonly root = new THREE.Group()

  private readonly scene = new THREE.Scene()
  private readonly pipeline: RenderPipeline | null
  private readonly hoverRing: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>
  private readonly targetDisc: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>
  private readonly renderContributor: RenderContributor
  private readonly removeSnapshotListener: (() => void) | null
  private disposed = false

  constructor(readonly options: SpatialCapsuleClickToWalkIndicatorsOptions) {
    this.pipeline = options.engine.context.hasService('RenderPipeline')
      ? options.engine.context.getService('RenderPipeline')
      : null
    this.root.name = 'spatialCapsuleClickToWalkIndicators'
    this.hoverRing = createClickToWalkRing({
      name: 'spatialCapsuleClickToWalkHoverRing',
      color: options.hoverColor ?? DEFAULT_HOVER_COLOR,
      opacity: options.hoverOpacity ?? DEFAULT_HOVER_OPACITY,
      radius: options.hoverRadius ?? CLICK_TO_WALK_HOVER_RING_RADIUS_M,
    })
    this.targetDisc = createClickToWalkDisc({
      name: 'spatialCapsuleClickToWalkTargetDisc',
      color: options.targetColor ?? DEFAULT_TARGET_COLOR,
      opacity: options.targetOpacity ?? DEFAULT_TARGET_OPACITY,
      radius: options.targetRadius ?? CLICK_TO_WALK_TARGET_DISC_RADIUS_M,
    })
    this.root.add(this.hoverRing, this.targetDisc)
    this.scene.add(this.root)

    this.renderContributor = {
      kind: 'spatial-capsule-click-to-walk-indicators',
      graphicsRequirements: DUAL_BACKEND_GRAPHICS_REQUIREMENTS,
      phases: [RenderPhase.MainOcclusionTested],
      renderPriority: options.renderPriority ?? DEFAULT_RENDER_PRIORITY,
      render: (frame, _phase, renderContext) => {
        this.render(frame, renderContext)
      },
    }
    this.pipeline?.addRenderContributor(this.renderContributor)
    this.removeSnapshotListener = options.controller.onSnapshotChange((snapshot) => {
      this.syncSnapshot(snapshot)
    })
  }

  dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    this.removeSnapshotListener?.()
    this.pipeline?.removeRenderContributor(this.renderContributor)
    disposeClickToWalkIndicator(this.hoverRing)
    disposeClickToWalkIndicator(this.targetDisc)
    this.root.removeFromParent()
    this.scene.remove(this.root)
  }

  private syncSnapshot(snapshot: SpatialCapsuleClickToWalkSnapshot): void {
    syncClickToWalkIndicator(this.hoverRing, {
      point: snapshot.available ? snapshot.hoverPoint : null,
      normal: snapshot.hoverNormal,
    })
    syncClickToWalkIndicator(this.targetDisc, {
      point: snapshot.active ? snapshot.targetPoint : null,
      normal: snapshot.targetNormal,
    })
  }

  private render(frame: ViewFrameContext, renderContext: PhaseRenderContext): void {
    if (!this.hoverRing.visible && !this.targetDisc.visible) {
      return
    }

    // 指示器独立 Scene，统一走受控 scene 提交；View 状态与 target 由 backend 决定。
    submitSceneInView(renderContext, this.scene, frame.camera)
  }
}

function createClickToWalkRing(options: {
  name: string
  color: THREE.ColorRepresentation
  opacity: number
  radius: number
}): THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial> {
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(options.radius - 0.04, options.radius, 72),
    new THREE.MeshBasicMaterial({
      color: options.color,
      transparent: true,
      opacity: options.opacity,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    })
  )
  ring.name = options.name
  ring.visible = false
  ring.renderOrder = 30
  return ring
}

function createClickToWalkDisc(options: {
  name: string
  color: THREE.ColorRepresentation
  opacity: number
  radius: number
}): THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial> {
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(options.radius, 72),
    new THREE.MeshBasicMaterial({
      color: options.color,
      transparent: true,
      opacity: options.opacity,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    })
  )
  disc.name = options.name
  disc.visible = false
  disc.renderOrder = 30
  return disc
}

function syncClickToWalkIndicator(
  indicator: THREE.Mesh,
  options: {
    point: THREE.Vector3 | null
    normal: THREE.Vector3 | null
  }
): void {
  if (!options.point) {
    indicator.visible = false
    return
  }

  const normal = normalizeClickToWalkIndicatorNormal(options.normal)
  indicator.position.copy(options.point).addScaledVector(normal, CLICK_TO_WALK_RING_OFFSET_M)
  indicator.quaternion.setFromUnitVectors(CLICK_TO_WALK_RING_UP, normal)
  indicator.visible = true
}

function normalizeClickToWalkIndicatorNormal(normal: THREE.Vector3 | null): THREE.Vector3 {
  if (!normal || normal.lengthSq() <= Number.EPSILON) {
    return CLICK_TO_WALK_RING_UP.clone()
  }

  return normal.clone().normalize()
}

function disposeClickToWalkIndicator(
  indicator: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>
): void {
  indicator.geometry.dispose()
  indicator.material.dispose()
}
